import { css } from 'styled-components';

const BASE_FONT_SIZE = 14;
const BASE_SPACING = 8;
const PADDING_WRAPPER = 20;
const MARGIN_TOP_SUB_COMPONENT = 30;
const MARGIN_BOTTOM_SUB_COMPONENT = 30;
const GLOBAL_WIDTH = 1000;

// Transforms a pixel value to rem
// round to 5 digits to match Foundation rem-calc
export function remCalc(pixelSize: number) {
  const remSize = pixelSize / BASE_FONT_SIZE;
  return `${parseFloat(remSize.toFixed(5))}rem`;
}

// Transforms a rem value to pixel (without px unit)
export function pixelCalc(remSize: string) {
  return parseFloat(remSize) * BASE_FONT_SIZE;
}

export const colors = {
  white: '#FFF',
  semiWhite: '#F8F9F8',
  darkBlue1: '#002136',
  darkBlue2: '#143145',
  darkBlue3: '#002238',
  lightBlue1: '#4895B6',
  lightBlue2: '#438BAA',
  lightBlue3: '#1d7398',
  lightBlue4: '#7BD6FE',
  green1: '#6AB556',
  green2: '#64A753',
  green3: '#3C8A2B',
  green4: '#CAF6C1',
  darkGreen1: '#49823D',
  lightGray1: '#EAEAEA',
  lightGray2: '#D7D7D7',
  mediumGray1: '#909090',
  mediumGray2: '#8E8E8E',
  darkGray1: '#888888',
  darkGray2: '#707070',
  darkGray3: '#666',
  darkGray4: '#505050',
  yellow1: '#FFD400',
  yellow2: '#E4B60B',
  yellow3: '#FDBB00',
  yellow4: '#A68724',
  yellow5: '#FFE991',
  red1: '#DB2A30',
  red2: '#C42128',
  red3: '#CB0808',
  red4: '#FFCCC1',
  backgroundDarkGray: 'rgba(0, 33, 54, 0.05)',
  backgroundLightGray: 'rgba(0, 0, 0, 0.06)',
  borderDarkGray: 'rgba(0, 0, 0, 0.85);',
  borderLightGray: 'rgba(0, 0, 0, 0.09);',
};

const layoutMap = {
  fontSizes: {
    base: {
      mobile: { value: 14, rem: remCalc(14) },
      desktop: { value: 14, rem: remCalc(14) },
    },
    small: {
      mobile: { value: 12, rem: remCalc(12) },
      desktop: { value: 12, rem: remCalc(12) },
    },
    h1: {
      desktop: { value: 64, rem: remCalc(64) },
      mobile: { value: 36, rem: remCalc(36) },
    },
    h2: {
      desktop: { value: 24, rem: remCalc(24) },
      mobile: { value: 20, rem: remCalc(20) },
    },
    h3: {
      desktop: { value: 18, rem: remCalc(18) },
      mobile: { value: 18, rem: remCalc(18) },
    },
    h4: {
      desktop: { value: 17, rem: remCalc(17) },
      mobile: { value: 17, rem: remCalc(17) },
    },
    h5: {
      desktop: { value: 17, rem: remCalc(17) },
      mobile: { value: 17, rem: remCalc(17) },
    },
    h6: {
      desktop: { value: 16, rem: remCalc(16) },
      mobile: { value: 16, rem: remCalc(16) },
    },
  },

  margins: {
    wrapper: {
      mobile: { value: 12, rem: remCalc(12) },
      desktop: { value: 10, rem: remCalc(10) },
    },

    component: {
      mobile: { value: 20, rem: remCalc(20) },
      desktop: { value: 30, rem: remCalc(30) },
    },
  },

  headerHeight: {
    expanded: { value: 140, rem: remCalc(140) },
    desktop: { value: 70, rem: remCalc(70) },
    mobile: { value: 50, rem: remCalc(50) },
    mobileWithSearch: { value: 115, rem: remCalc(115) },
    mobileExpanded: { value: 145, rem: remCalc(145) },
  },

  fontFamily: 'Open Sans, Helvetica Neue, Helvetica, Arial, Roboto, sans-serif',

  sidebarContentWidth: {
    desktop: { value: 304, rem: remCalc(304) },
  },

  sidebarMenuWidth: {
    desktop: { value: 56, rem: remCalc(56) },
  },

  pageContent: {
    gutter: { value: 10, rem: remCalc(10) },
    maxWidth: { value: 1300, rem: remCalc(1300) },
  },

  siteWidth: { value: GLOBAL_WIDTH, rem: remCalc(GLOBAL_WIDTH) },
  borderRadius: '5px',
  lineHeight: 1.5,

  stickyNavigationHeight: { value: 50, rem: remCalc(50) },

  dragAndDropHandleSize: {
    mobile: {
      value: 32,
      rem: remCalc(32),
    },
    desktop: { value: 16, rem: remCalc(16) },
  },

  pageContentMaxWidthVal: 1300,
  pageContentGutterVal: 10,

  // Ad layout
  takeOverTopMargin: '120px',
  takeOverWidth: '1600px',
  takeOverHeight: '900px',
  outsiderMinWidth: '200px',

  listSideMargin: '0.6rem',
  listSidePadding: '0.6rem',
  listSideHeight: '2.6rem',

  // Product card
  productCardMinWidthMobile: '146px',
  productCardMinWidth: '200px',
  productCardMaxWidth: '255px',
  productCardImageSizeSmall: '90px',
  productCardImageSizeLarge: '130px',
  productCardPadding: '1rem',

  // Foundation grid
  gridColumnGutter: {
    small: { value: 25, rem: remCalc(25) },
    large: { value: PADDING_WRAPPER, rem: remCalc(PADDING_WRAPPER) },
  },

  // Search
  searchBar: {
    height: {
      desktop: {
        value: 45,
        rem: remCalc(45),
      },
      mobile: {
        value: 35,
        rem: remCalc(35),
      },
    },
    borderWidth: 1,
  },

  // Spacing
  spacing: {
    xxSmall: `calc(0.5 * ${remCalc(BASE_SPACING)})`,
    xSmall: `calc(1 * ${remCalc(BASE_SPACING)})`,
    small: `calc(2 * ${remCalc(BASE_SPACING)})`,
    medium: `calc(3 * ${remCalc(BASE_SPACING)})`,
    large: `calc(4 * ${remCalc(BASE_SPACING)})`,
    xLarge: `calc(5 * ${remCalc(BASE_SPACING)})`,
    xxLarge: `calc(6 * ${remCalc(BASE_SPACING)})`,
    values: {
      xxSmall: 0.5 * BASE_SPACING,
      xSmall: 1 * BASE_SPACING,
      small: 2 * BASE_SPACING,
      medium: 3 * BASE_SPACING,
      large: 4 * BASE_SPACING,
      xLarge: 5 * BASE_SPACING,
      xxLarge: 6 * BASE_SPACING,
    },
    rem: {
      xxSmall: (0.5 * BASE_SPACING) / BASE_FONT_SIZE,
      xSmall: (1 * BASE_SPACING) / BASE_FONT_SIZE,
      small: (2 * BASE_SPACING) / BASE_FONT_SIZE,
      medium: (3 * BASE_SPACING) / BASE_FONT_SIZE,
      large: (4 * BASE_SPACING) / BASE_FONT_SIZE,
      xLarge: (5 * BASE_SPACING) / BASE_FONT_SIZE,
      xxLarge: (6 * BASE_SPACING) / BASE_FONT_SIZE,
    },
  },

  animations: {
    buttonTransition: '150ms ease',
  },
};

const layoutMapDeprecated = {
  paddingWrapperVal: PADDING_WRAPPER,
  paddingWrapper: remCalc(PADDING_WRAPPER),
  marginTopSubComponentVal: MARGIN_TOP_SUB_COMPONENT,
  marginTopSubComponent: remCalc(MARGIN_TOP_SUB_COMPONENT),
  marginBottomSubComponentVal: MARGIN_BOTTOM_SUB_COMPONENT,
  marginBottomSubComponent: remCalc(MARGIN_BOTTOM_SUB_COMPONENT),
};

const fullLayoutMap = {
  ...layoutMapDeprecated,
  ...layoutMap,
};

export const layout =
  process.env.NODE_ENV === 'development'
    ? new Proxy(fullLayoutMap, {
        get: (obj, prop) => {
          if (prop in layoutMapDeprecated) {
            // eslint-disable-next-line no-console
            console.warn(
              `Deprecation: layout['${String(
                prop
              )}'] is deprecated in base, please refer to @prisjakt/ui-base to see the new value.`
            );
          }
          return obj[prop];
        },
      })
    : fullLayoutMap;

// Foundation breakpoints
export const breakpoints = {
  smallOnly: 'screen and (max-width: 39.9375em)',
  mediumUp: 'screen and (min-width: 40em)',
  mediumOnly: 'screen and (max-width: 63.9375em) and (min-width: 40em)',
  mediumDown: 'screen and (max-width: 63.9375em)',
  largeUp: 'screen and (min-width: 64em)',
  largeOnly: 'screen and (max-width: 74.9375em) and (min-width: 64em)',
  hideOutsiderAd: 'screen and (max-width: 1199px)',
  showOutsiderAd: 'screen and (min-width: 1200px)',
};

// Create media queries from a list of breakpoints
type MediaType = {
  [key in keyof typeof breakpoints]: typeof css;
};
type Parameters<T> = T extends (...args: infer T) => any ? T : never;
export const media: MediaType = Object.entries(breakpoints).reduce(
  (accumulator, [key, value]) => ({
    ...accumulator,
    [key]: ((...args: Parameters<typeof css>) => css`
      @media ${value} {
        ${css(...args)};
      }
    `) as typeof css,
  }),
  {} as MediaType
);
